const CartItemDiscount = require('@/libraries/DigiTickets/Offers/CartItemDiscount');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const { preciseRound } = require('@/functions/math');

/**
 * @constructor
 *
 * @param {Hydrator} hydrator
 * @param {LangService} LangService
 * @param {OfferRepository} offerRepository
 */
class OfferApplicator {
    constructor(
        hydrator,
        langService,
        offerRepository
    ) {
        this.hydrator = hydrator;
        this.langService = langService;
        this.offerRepository = offerRepository;
    }

    /**
     * Apply automatic offers returned by API.
     *
     * @param {CartService} cartService
     * @param {object} offerResponse Offer calculation results from server.
     *
     * @returns {Promise}
     */
    async applyOffersToCart(cartService, offerResponse) {
        // The offerResponse is the discounts object from the /cartcalculations API endpoint.
        // The response is structured as:
        // {
        //   "1": [ // Line number
        //     { // CartItemDiscount data
        //       "offerID": "offer_123",
        //       "amount": -5.00,
        //       "lineThirdPartyRef": "my-cool-line-1",
        //       ...
        //     },
        //     { // Another CartItemDiscount for line 1 ... },
        //   ],
        //   "2": [ // Line number 2
        //     { // CartItemDiscount data ... },
        //   ],
        //   ...
        // }
        // We no longer want to use the line number to match lines as it is unreliable as it's based on the order
        // of the lines, but the structure has not been changed for backwards compatibility.
        // Instead, we will match lines using the lineThirdPartyRef property on each of the CartItemDiscounts.
        // So first of all lets flatten the offerResponse into an array of discounts with their lineThirdPartyRef.
        const discountsByLineThirdPartyRef = {};
        Object.values(offerResponse).forEach((discountsForLine) => {
            discountsForLine.forEach((discountData) => {
                const lineRef = discountData.lineThirdPartyRef;
                if (!discountsByLineThirdPartyRef[lineRef]) {
                    discountsByLineThirdPartyRef[lineRef] = [];
                }
                discountsByLineThirdPartyRef[lineRef].push(discountData);
            });
        });

        // Now we have an object like:
        // {
        //   "my-cool-line-1": [ {CartItemDiscount data}, ... ],
        //   "my-cool-line-2": [ {CartItemDiscount data}, ... ],
        //   ...
        // }
        // Lets apply each of those discounts to the correct line in the cart.
        for (let lineRef in discountsByLineThirdPartyRef) {
            // Make linting happy.
            if (!discountsByLineThirdPartyRef.hasOwnProperty(lineRef)) {
                continue;
            }

            // Find this line in the cart by its third party ref - more reliable than line number.
            const line = cartService.currentCart.getLineByThirdPartyRef(lineRef);
            if (!line) {
                console.error(`Line with thirdPartyRef ${lineRef} not found in cart but returned by cart calculations API.`);
                continue;
            }

            const discountsForLine = discountsByLineThirdPartyRef[lineRef];
            for (let i = 0; i < discountsForLine.length; i++) {
                /** @type {CartItemDiscount} */
                let itemDiscount = this.hydrator.hydrate(
                    discountsForLine[i],
                    new CartItemDiscount()
                );

                if (itemDiscount.offerID) {
                    let offer;
                    try {
                        offer = await this.offerRepository.find(itemDiscount.offerID);
                    } catch (e) {
                    }
                    itemDiscount.offer = offer || null;
                }

                if (itemDiscount.reason === PaymentMethodRef.GIFT_VOUCHER) {
                    itemDiscount.description = this.langService.getText('GIFT_VOUCHERS.VOUCHER');
                }

                line.addDiscount(itemDiscount);
            }
        }

        await cartService.modifyCurrentCart(() => {});
    }

    /**
     * Apply manual offers/discounts selected by operator.
     *
     * This loops through each item in the cart. It removes any existing CartItemDiscounts from manual offers.
     *
     * If the line has a manual offer set it creates a CartItemDiscount for that offer, and deactivates all other
     * offers on the line.
     *
     * If it does not have a manual offer it activates all CartItemDiscounts on the line.
     *
     * @param {CartService} cart
     */
    applyManualLineOffers(cart) {
        for (let i = 0; i < cart.itemList.length; i++) {
            /** @type {CartLine} */
            const cartItem = cart.itemList[i];

            const manualOffer = cartItem.getManualOffer();

            // A manual discount overrides all other discounts on the line.
            // Manual discounts may be predetermined or variable.
            // This is controlled by the order of the discounts @see CartLine.sortDiscounts()
            // Disable any existing discounts.
            let existingDiscounts = cartItem.getDiscounts();

            // Reverse loop so the splicing doesn't affect the position
            for (let d = existingDiscounts.length - 1; d >= 0; d--) {
                const lineDiscount = existingDiscounts[d];

                if (lineDiscount.manual) {
                    // Remove any existing manual discount.
                    existingDiscounts = existingDiscounts.splice(d, 1);
                } else {
                    // This is an order level discount OR an experience voucher.
                    // If we have a manual discount then active = false otherwise active = true.
                    lineDiscount.setActive(!manualOffer);
                }
            }

            if (manualOffer) {
                const manualItemDiscount = new CartItemDiscount();
                manualItemDiscount.offer = manualOffer;
                manualItemDiscount.offerID = manualOffer.ID;

                manualItemDiscount.manual = true;
                manualItemDiscount.qty = cartItem.getQuantity();

                switch (manualOffer.valueType) {
                    case 'percent':
                        manualItemDiscount.percentage = manualOffer.value;
                        manualItemDiscount.amount = preciseRound(
                            (manualOffer.value * (cartItem.baseTotal / 100)),
                            2
                        );
                        break;
                    case 'value':
                    case 'variable':
                        // Note: variable discounts used to apply to only a single instance on a line but apply to all
                        // instances on a line after PROP-965.
                        manualItemDiscount.amount = manualOffer.value * cartItem.quantity;
                        if (Math.abs(manualItemDiscount.amount) > Math.abs(cartItem.baseTotal)) {
                            manualItemDiscount.amount = cartItem.baseTotal;
                        }
                        manualItemDiscount.percentage = preciseRound(
                            (manualItemDiscount.amount * 100) / cartItem.baseTotal,
                            4
                        );
                        break;
                }

                cartItem.addDiscount(manualItemDiscount);
            }
        }
    }
}

module.exports = OfferApplicator;
